import { ref, computed, watch } from 'vue'

export interface CartItem {
  _id: string
  product: any // Full product object for backend compatibility
  quantity: number
  selectedSize?: {
    name: string
    label: string
    stockQuantity?: number
    additionalPricing?: Array<{
      currency: string
      additionalPrice: number
    }>
  }
  addedAt: Date
}

const CART_STORAGE_KEY = 'artfield_cart'
const CART_OPEN_STORAGE_KEY = 'artfield_cart_open'

// Initialize cart items and UI state
const cartItems = ref<CartItem[]>([])
const isCartOpen = ref(false)

// Save cart to localStorage
const saveToStorage = () => {
  // Only save to localStorage on client-side
  if (process.client && typeof window !== 'undefined') {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems.value))
    } catch (error) {
      console.error('Error saving cart to localStorage:', error)
    }
  }
}

// Load cart open state from localStorage
const loadCartOpenState = () => {
  if (typeof window === 'undefined') return false
  
  try {
    const stored = localStorage.getItem(CART_OPEN_STORAGE_KEY)
    return stored === 'true'
  } catch (error) {
    console.error('Error loading cart open state:', error)
    return false
  }
}

// Save cart open state to localStorage
const saveCartOpenState = (isOpen: boolean) => {
  if (typeof window === 'undefined') return
  
  try {
    localStorage.setItem(CART_OPEN_STORAGE_KEY, isOpen.toString())
  } catch (error) {
    console.error('Error saving cart open state:', error)
  }
}

const loadFromStorage = () => {
  // Only load from localStorage on client-side
  if (process.client && typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Validate the parsed data structure
        if (Array.isArray(parsed)) {
          cartItems.value = parsed.map((item: any) => ({
            ...item,
            addedAt: new Date(item.addedAt)
          }))
        }
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error)
      cartItems.value = []
    }
  }
}

export const useCartStore = () => {
  // Load cart data only on client-side mount
  if (process.client && cartItems.value.length === 0) {
    loadFromStorage()
  }

  const addToCart = (product: any, quantity: number = 1, selectedSize?: any) => {
    if (!product?.pricing?.length) {
      throw new Error('Product price not available')
    }

    // Create unique ID based on product and size
    const cartItemId = selectedSize 
      ? `${product._id}-${selectedSize.name}` 
      : product._id

    // Check if item already exists in cart
    const existingItemIndex = cartItems.value.findIndex(item => item._id === cartItemId)
    
    if (existingItemIndex > -1) {
      // Update existing item quantity
      const existingItem = cartItems.value[existingItemIndex]
      const newQuantity = existingItem.quantity + quantity
      
      // Check stock limits
      if (selectedSize && selectedSize.stockQuantity && newQuantity > selectedSize.stockQuantity) {
        throw new Error('Insufficient stock')
      }
      
      cartItems.value[existingItemIndex].quantity = newQuantity
    } else {
      // Add new item to cart
      const cartItem: CartItem = {
        _id: cartItemId,
        product: product, // Store full product object
        quantity,
        selectedSize: selectedSize ? {
          name: selectedSize.name,
          label: selectedSize.label,
          stockQuantity: selectedSize.stockQuantity,
          additionalPricing: selectedSize.additionalPricing // Include additional pricing
        } : undefined,
        addedAt: new Date()
      }

      cartItems.value.push(cartItem)
    }

    // Save to localStorage
    saveToStorage()

    // Open cart panel after adding
    isCartOpen.value = true
  }

  const removeFromCart = (cartItemId: string) => {
    const index = cartItems.value.findIndex(item => item._id === cartItemId)
    if (index > -1) {
      cartItems.value.splice(index, 1)
      saveToStorage()
    }
  }

  const updateQuantity = (cartItemId: string, quantity: number) => {
    const item = cartItems.value.find(item => item._id === cartItemId)
    if (item) {
      if (quantity <= 0) {
        removeFromCart(cartItemId)
      } else {
        // Check stock limits
        if (item.selectedSize?.stockQuantity && quantity > item.selectedSize.stockQuantity) {
          throw new Error('Insufficient stock')
        }
        item.quantity = quantity
        saveToStorage()
      }
    }
  }

  const clearCart = () => {
    cartItems.value = []
    saveToStorage()
  }

  const getProductPrice = (product: any, selectedSize?: any) => {
    if (!product?.pricing?.length) return null
    
    let basePrice = product.pricing[0] // Get first pricing
    
    // If size is selected and has additional pricing
    if (selectedSize?.additionalPricing?.length) {
      const sizePrice = selectedSize.additionalPricing.find(
        (ap: any) => ap.currency === basePrice.currency
      )
      if (sizePrice) {
        return {
          ...basePrice,
          basePrice: basePrice.basePrice + sizePrice.additionalPrice,
          salePrice: basePrice.salePrice ? basePrice.salePrice + sizePrice.additionalPrice : undefined
        }
      }
    }
    
    return basePrice
  }

  const getCurrencySymbol = (currencyCode: string): string => {
    const currencies: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      TRY: '₺'
    }
    return currencies[currencyCode] || currencyCode
  }

  const formatPrice = (price: number): string => {
    return price.toFixed(2)
  }

  const openCart = () => {
    isCartOpen.value = true
  }

  const closeCart = () => {
    isCartOpen.value = false
  }

  const toggleCart = () => {
    isCartOpen.value = !isCartOpen.value
  }

  // Watch for changes in isCartOpen and save to localStorage
  watch(isCartOpen, (newValue) => {
    saveCartOpenState(newValue)
  })

  // Computed properties
  const cartCount = computed(() => {
    return cartItems.value.reduce((total, item) => total + item.quantity, 0)
  })

  const cartSubtotal = computed(() => {
    return cartItems.value.reduce((total, item) => {
      const price = getProductPrice(item.product, item.selectedSize)
      if (price) {
        const finalPrice = price.isOnSale ? price.salePrice : price.basePrice
        return total + (finalPrice * item.quantity)
      }
      return total
    }, 0)
  })

  const cartCurrency = computed(() => {
    // Get currency from first item's product
    if (cartItems.value.length > 0) {
      const firstProduct = cartItems.value[0].product
      if (firstProduct?.pricing?.length > 0) {
        return firstProduct.pricing[0].currency
      }
    }
    return 'EUR'
  })

  const hasItems = computed(() => cartItems.value.length > 0)

  // Helper function to get cart items in format suitable for backend
  const getCartForCheckout = () => {
    return cartItems.value.map(item => ({
      productId: item.product._id,
      product: item.product,
      quantity: item.quantity,
      selectedSize: item.selectedSize,
      price: getProductPrice(item.product, item.selectedSize),
      addedAt: item.addedAt
    }))
  }

  // Initialize cart open state from localStorage on first access
  if (typeof window !== 'undefined') {
    isCartOpen.value = loadCartOpenState()
  }

  return {
    // State
    cartItems: readonly(cartItems),
    isCartOpen: readonly(isCartOpen),
    
    // Actions
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    loadFromStorage,
    
    // Computed
    cartCount,
    cartSubtotal,
    cartCurrency,
    hasItems,
    
    // Utilities
    getCurrencySymbol,
    formatPrice,
    getProductPrice,
    getCartForCheckout
  }
} 